import * as React from 'react';
import classnames from 'classnames';
import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';

import ProfileCard from 'components/cards/profile-card/ProfileCard';
import Popover from 'components/popover/Popover';

import ProfileCardGroup from './card-group/ProfileCardGroup';

import { __t, applyTranslations } from 'utils/language';
import { hasFashionIdentity, hasSelfIdentity } from '../influencer-edit-profile/utils';

import type { InfluencerProfile } from 'types';

import './influencer-about-tab.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./influencer-about-tab.i18n.json'));

import type { ProfileCardGroupIcon } from './card-group/types';

interface Props {
  influencerProfile: InfluencerProfile
}

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'influencer_about_tab';

const InfluencerAboutTab: React.FC<Props> = ({
  influencerProfile
}): JSX.Element => {
  const getContainerClasses = (
    type: 'two-column'
  ): string => (
    classnames('influencer-about-tab__container', {
      [`influencer-about-tab__container--${type}`]: Boolean(type)
    })
  );

  const getCardBodyClasses = (
    withCategoryPill: boolean = false
  ): string => (
    classnames('influencer-about-tab__card-body', {
      'influencer-about-tab__card-body--category-pill': withCategoryPill
    })
  );

  const hasFashionTags = (
    identifier: string
  ): boolean => (
    !isEmpty(influencerProfile[camelCase(identifier)])
  );

  const hasJob = (): boolean => {
    const job = influencerProfile.profession;

    return Boolean(job) && job !== 'none';
  };

  const renderSelfIdentityPopover = (): React.ReactNode => (
    <Popover showQuestionMark>
      <div className="influencer-about-tab__popover-body">
        {__t(`${MSG_KEY}.popovers.self_identity`)}
      </div>
    </Popover>
  );

  const renderFashionSizesPopover = (): React.ReactNode => {
    const sharedMsgKey = `${MSG_KEY}.popovers.fashion_sizes`;

    const getText = (key: string): React.ReactNode => (
      <div>
        <strong>
          {__t(`${sharedMsgKey}.${key}`)}
        </strong>:&nbsp;
        <span>
          {__t(`${sharedMsgKey}.${key}_text`)}
        </span>
      </div>
    );

    return (
      <Popover showQuestionMark>
        <div className="influencer-about-tab__popover-body">
          {getText('adaptive')}
          {getText('petite')}
          {getText('plus_size')}
          {getText('standard_manufacturer_sizing')}
          {getText('tall')}
        </div>
      </Popover>
    );
  };

  const renderFashionIdentityCardGroup = (
    identifier: string,
    icon: ProfileCardGroupIcon,
    popover?: React.ReactNode
  ): React.ReactNode => (
    <ProfileCardGroup
      identifier={identifier}
      items={(influencerProfile[camelCase(identifier)] || []).map(item => ({
        identifier: item,
        label: __t(`${COMMON_MSG_KEY}.${snakeCase(identifier)}.${item}`)
      }))}
      label={{
        value: __t(`${MSG_KEY}.labels.${snakeCase(identifier)}`),
        popover,
        icon: {
          name: icon.name,
          width: icon.width,
          height: icon.height,
          className: 'influencer-about-tab__icon'
        }
      }}
    />
  );

  const renderBio = (): React.ReactNode => {
    const pitch = influencerProfile.pitch;

    if (!pitch && !hasJob()) {
      return (
        <div className="influencer-about-tab__card">
          {__t(`${MSG_KEY}.messages.no_description`)}
        </div>
      );
    }

    if (pitch) {
      return (
        <div className="influencer-about-tab__card">
          <div className="influencer-about-tab__card-heading">
            {__t(`${MSG_KEY}.labels.bio`)}
          </div>
          <div className={getCardBodyClasses()}>
            {pitch}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderJob = (): React.ReactNode => {
    const job = influencerProfile.profession;

    if (!hasJob()) {
      return null;
    }

    return (
      <div className="influencer-about-tab__card">
        <ProfileCardGroup
          identifier="jobs"
          items={['jobs'].map(identifier => ({
            identifier,
            label: __t(`${COMMON_MSG_KEY}.${identifier}.${job}`)
          }))}
          label={{
            value: __t(`${MSG_KEY}.labels.primary_job`)
          }}
        />
      </div>
    );
  };

  const renderFashionIdentity = (): React.ReactNode => (
    <div className="influencer-about-tab__card influencer-about-tab__card--with-group">
      {
        hasFashionTags('fashion-styles') &&
          renderFashionIdentityCardGroup('fashion-styles', {
            name: 'hanger-custom-fg',
            width: 20,
            height: 15
          })
      }
      {
        hasFashionTags('fashion-categories') &&
          renderFashionIdentityCardGroup('fashion-categories', {
            name: 'diamond-square-circle-custom-fg',
            width: 18,
            height: 18
          })
      }
      {
        hasFashionTags('fashion-sizes') &&
          renderFashionIdentityCardGroup('fashion-sizes', {
            name: 'tape-measure-custom-fg',
            width: 20,
            height: 20
          }, renderFashionSizesPopover())
      }
    </div>
  );

  const renderGender = (): React.ReactNode => (
    <ProfileCardGroup
      identifier="gender"
      items={['Gender', 'GenderAdditional'].map(identifier => {
        const section = snakeCase(identifier);
        const key = (influencerProfile[`influencer${identifier}`] ?? '').toLowerCase();
        let label: string | null = __t(`${COMMON_MSG_KEY}.${section}.${key}`);

        if (key === 'self_describe') {
          label = influencerProfile.influencerGenderSelfDescribed ?? '';
        }

        if (key === 'no_answer') {
          label = '';
        }

        return {
          identifier,
          label
        };
      })}
      label={{
        value: __t(`${MSG_KEY}.labels.gender`)
      }}
    />
  );

  const renderEthnicity = (): React.ReactNode => {
    const ethnicGroup = influencerProfile.ethnicGroup;

    if (isNil(ethnicGroup) || ethnicGroup === 'none') {
      return null;
    }

    return (
      <ProfileCardGroup
        identifier="ethnic-group"
        items={['ethnicGroup'].map(identifier => {
          const section = snakeCase(identifier);
          const key = influencerProfile[identifier] ?? '';

          return {
            identifier,
            label: __t(`${COMMON_MSG_KEY}.${section}.${key}`)
          };
        })}
        label={{
          value: __t(`${MSG_KEY}.labels.ethnicity`)
        }}
      />
    );
  };

  const renderSelfIdentity = (): React.ReactNode => (
    <div className="influencer-about-tab__card influencer-about-tab__card--with-group">
      {renderGender()}
      {renderEthnicity()}
    </div>
  );

  return (
    <div className="influencer-about-tab">
      <div className={getContainerClasses('two-column')}>
        <div className="influencer-about-tab__column">
          <ProfileCard
            title={{
              text: __t(`${MSG_KEY}.labels.about`)
            }}
          >
            {renderBio()}
            {renderJob()}
          </ProfileCard>
        </div>
        <div className="influencer-about-tab__column">
          {
            hasFashionIdentity(influencerProfile) && (
              <ProfileCard
                title={{
                  text: __t(`${MSG_KEY}.labels.fashion_identity`)
                }}
              >
                {renderFashionIdentity()}
              </ProfileCard>
            )
          }
          {
            hasSelfIdentity(influencerProfile, 'view') && (
              <ProfileCard
                title={{
                  text: __t(`${MSG_KEY}.labels.self_identity`),
                  popover: renderSelfIdentityPopover()
                }}
              >
                {renderSelfIdentity()}
              </ProfileCard>
            )
          }
        </div>
      </div>
    </div>
  );
};

export default InfluencerAboutTab;
